/**
 * @license Copyright (c) 2014-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

import { ClassicEditor } from '@ckeditor/ckeditor5-editor-classic';

import { Alignment } from '@ckeditor/ckeditor5-alignment';
import { Autoformat } from '@ckeditor/ckeditor5-autoformat';
import {
    Bold,
    Code,
    Italic,
    Strikethrough,
    Subscript,
    Superscript,
    Underline
} from '@ckeditor/ckeditor5-basic-styles';
import { BlockQuote } from '@ckeditor/ckeditor5-block-quote';
import { SourceEditing } from '@ckeditor/ckeditor5-source-editing';
import { CaseChange } from '@ckeditor/ckeditor5-case-change';
import { CloudServices } from '@ckeditor/ckeditor5-cloud-services';
import { CodeBlock } from '@ckeditor/ckeditor5-code-block';
import { Plugin, type EditorConfig } from '@ckeditor/ckeditor5-core';
import { Essentials } from '@ckeditor/ckeditor5-essentials';
import { FindAndReplace } from '@ckeditor/ckeditor5-find-and-replace';
import { FontBackgroundColor, FontColor, FontFamily, FontSize } from '@ckeditor/ckeditor5-font';
import { FormatPainter } from '@ckeditor/ckeditor5-format-painter';
import { Heading, Title } from '@ckeditor/ckeditor5-heading';
import { Highlight } from '@ckeditor/ckeditor5-highlight';
import { HorizontalLine } from '@ckeditor/ckeditor5-horizontal-line';
import { DataSchema, FullPage, GeneralHtmlSupport } from '@ckeditor/ckeditor5-html-support';
import {
    AutoImage,
    Image,
    ImageCaption,
    ImageInsert,
    ImageResize,
    ImageStyle,
    ImageToolbar,
    ImageResizeEditing,
    ImageResizeHandles,
    ImageUpload,
    PictureEditing
} from '@ckeditor/ckeditor5-image';
import { Indent,IndentBlock } from '@ckeditor/ckeditor5-indent';
import { List, ListProperties, TodoList } from '@ckeditor/ckeditor5-list';
import { Markdown } from '@ckeditor/ckeditor5-markdown-gfm';
import { Paragraph } from '@ckeditor/ckeditor5-paragraph';
import { StandardEditingMode } from '@ckeditor/ckeditor5-restricted-editing';
import { RestrictedEditingMode } from '@ckeditor/ckeditor5-restricted-editing';
import { SelectAll } from '@ckeditor/ckeditor5-select-all';
import {
    SpecialCharacters,
    // SpecialCharactersArrows,
    SpecialCharactersCurrency,
    SpecialCharactersEssentials,
    SpecialCharactersLatin,
    SpecialCharactersMathematical,
    SpecialCharactersText
} from '@ckeditor/ckeditor5-special-characters';
import { Style } from '@ckeditor/ckeditor5-style';
import {
    Table,
    TableCaption,
    TableCellProperties,
    TableColumnResize,
    TableProperties,
    TableToolbar
} from '@ckeditor/ckeditor5-table';
import { Template } from '@ckeditor/ckeditor5-template';
import { TextTransformation } from '@ckeditor/ckeditor5-typing';
import { Undo } from '@ckeditor/ckeditor5-undo';
import { EditorWatchdog } from '@ckeditor/ckeditor5-watchdog';
import { WordCount } from '@ckeditor/ckeditor5-word-count';
import Tooltip from "../custom-plugins/tooltip/tooltip";
import ImagePalette from "../custom-plugins/images/images";
import { HtmlEmbed } from '@ckeditor/ckeditor5-html-embed';
import MathType from '@wiris/mathtype-ckeditor5';
import Calculator from "../custom-plugins/calculator/calculator";
import Wiris from "../custom-plugins/wiris/wiris";
import Templates from "../custom-plugins/templates/templates"
import Specialchar from "../custom-plugins/specialchar/specialchar"
import PasteGuard from "../custom-plugins/paste-guard/paste-guard";
// You can read more about extending the build with additional plugins in the "Installing plugins" guide.
// See https://ckeditor.com/docs/ckeditor5/latest/installation/plugins/installing-plugins.html for details.

class Editor extends ClassicEditor {
    public static override builtinPlugins = [
        SourceEditing,
        Alignment,
        // AutoImage,
        Autoformat,
        // Autosave,
        BlockQuote,
        Bold,
        // CKBox,
        // CKBoxImageEdit,
        // CaseChange,
        CloudServices,
        Code,
        CodeBlock,
        // DataSchema,
        Essentials,
        FindAndReplace,
        FontBackgroundColor,
        FontColor,
        FontFamily,
        FontSize,
        // FormatPainter,
        FullPage,
        GeneralHtmlSupport,
        Heading,
        Highlight,
        HorizontalLine,
        Image,
        ImageInsert,
        ImageCaption,
        ImageResize,
        ImageStyle,
        ImageToolbar,
        ImageResizeEditing,
        ImageResizeHandles,
        // ImageUpload,
        Indent,
        IndentBlock,
        Italic,
        List,
        ListProperties,
        Markdown,
        // MediaEmbed,
        // MediaEmbedToolbar,
        Paragraph,
        // PasteFromOffice,
        PictureEditing,
        // RevisionHistory,
        SelectAll,
        SpecialCharacters,
        // SpecialCharactersArrows,
        SpecialCharactersCurrency,
        // SpecialCharactersEssentials,
        SpecialCharactersLatin,
        SpecialCharactersMathematical,
        SpecialCharactersText,
        Specialchar,
        StandardEditingMode,
        Strikethrough,
        Style,
        Subscript,
        Superscript,
        Table,
        TableCaption,
        TableCellProperties,
        TableColumnResize,
        TableProperties,
        TableToolbar,
        // Template,
        TextTransformation,
        // Title,
        TodoList,
        Underline,
        Undo,
        WordCount,
        RestrictedEditingMode,
        Tooltip,
        ImagePalette,
        Calculator,
        HtmlEmbed,
        // MathType,
        Wiris,
        Templates,
        PasteGuard
    ];

    public static override defaultConfig: EditorConfig = {
        // removePlugins: [ 'RestrictedEditingMode' ],
        toolbar: {
            items: [
                'heading',
                'style',
                '|',
                'bold',
                'italic',
                'bulletedList',
                'numberedList',
                '|',
                'outdent',
                'indent',
                '|',
                'blockQuote',
                'insertTable',
                'mediaEmbed',
                'undo',
                'redo',
                '|',
                'alignment',
                'caseChange',
                'codeBlock',
                'code',
                'fontColor',
                'fontSize',
                'fontBackgroundColor',
                'findAndReplace',
                'fontFamily',
                'horizontalLine',
                'formatPainter',
                'highlight',
                'selectAll',
                'revisionHistory',
                '|',
                'specialCharacters',
                'wiris',
                '|',
                'restrictedEditingException',
                'superscript',
                'subscript',
                'insertTemplate',
                'todoList',
                'underline',
                'sourceEditing',
                'tooltip',
                'imagePalette',
                'calculator',
                '|',
                'templates'

            ],
            shouldNotGroupWhenFull: true
        },
        image: {
            resizeOptions: [
                {
                    name: 'resizeImage:original',
                    value: null,
                    label: 'Original'
                },
                {
                    name: 'resizeImage:40',
                    value: '40',
                    label: '40%'
                },
                {
                    name: 'resizeImage:60',
                    value: '60',
                    label: '60%'
                }
            ],
            toolbar: [
                'toggleImageCaption',
                '|',
                'imageStyle:inline',
                'imageStyle:wrapText',
                'imageStyle:breakText',
                'imageStyle:side',
                '|',
                'resizeImage',
            ],
        },
        table: {
            contentToolbar: [
                'tableColumn',
                'tableRow',
                'mergeTableCells',
                'tableCellProperties',
                'tableProperties'
            ]
        },
        fontSize: {
            options: [
                9,
                11,
                13,
                17,
                19,
                'default',
            ]
        },
        fontFamily: {
            options: [
                'default',
                'Times New Roman, Times, serif ',
                'Courier New, Courier, monospace',
                'Verdana,Arial,sans-serif',
            ]
        },
        htmlEmbed: {
            showPreviews: true,
            sanitizeHtml: (inputHtml) => {
                return {
                    html: inputHtml,
                    hasChanged: true
                };
            }
        },
        indentBlock: {
            offset: 1,
            unit: 'em'
        },
        specialCharacters: {
            order: [
                'Specialchar',
                'Text',
                'Latin',
                'Mathematical',
                'Currency',
                'Arrows'
            ]
        },
        htmlSupport: {
            allow: [
                {
                    name: /.*/,
                    attributes: true,
                    classes: true,
                    styles: true
                }
            ]
        },
        
    };
}

export default Editor;
